import type { ModelInfo, ProviderMetadata } from '../providers'

import { listModels } from '@xsai/model'

import { isAbsoluteHttpUrl } from '../../utils/url'
import { extractModelCapabilities } from '../../utils/vision'

/**
 * 规范化 OpenAI 兼容 API 的 baseUrl
 * 支持以下格式输入：
 * - https://xxx.com                      → https://xxx.com/v1/
 * - https://xxx.com/                     → https://xxx.com/v1/
 * - https://xxx.com/v1                   → https://xxx.com/v1/
 * - https://xxx.com/v1/                  → https://xxx.com/v1/
 * - https://open.bigmodel.cn/api/paas/v4 → https://open.bigmodel.cn/api/paas/v4/ (智谱)
 * - https://xxx.com/v2                   → https://xxx.com/v2/ (其他使用非 v1 版本的 API)
 */
export function normalizeBaseUrl(url: string): string {
  const trimmed = url.trim()
  if (!trimmed)
    return ''
  // 兼容用户直接粘贴完整端点（/chat/completions 或 /responses），
  // 避免运行时再次拼接成 /chat/completions/chat/completions。
  const pastedEndpoint = /\/(?:chat\/completions|responses)$/i.test(trimmed.replace(/\/+$/, ''))
  let normalized = trimmed
    .replace(/\/+$/, '')
    .replace(/\/(?:chat\/completions|responses)$/i, '')
  // 检测是否已包含版本号路径（/v1, /v2, /v3, /v4 等）
  // 如果已有版本号，保持原样；否则添加 /v1
  if (!pastedEndpoint && !/\/v\d+$/.test(normalized)) {
    normalized += '/v1'
  }
  return `${normalized}/`
}

function describeHttpError(status: number, action: string): string {
  if (status === 401)
    return `${action}失败：API Key 无效或已过期`
  if (status === 403)
    return `${action}失败：API Key 权限不足`
  if (status === 429)
    return `${action}失败：请求过于频繁，请稍后再试`
  if (status >= 500)
    return `${action}失败：服务器内部错误（${status}）`
  return `${action}失败：服务器返回错误（HTTP ${status}）`
}

type ProviderCreator = (apiKey: string, baseUrl: string) => any

export function buildOpenAICompatibleProvider(
  options: Partial<ProviderMetadata> & {
    id: string
    name: string
    icon: string
    description: string
    nameKey: string
    descriptionKey: string
    category?: 'chat' | 'embed' | 'speech'
    tasks?: string[]
    defaultBaseUrl?: string
    creator: ProviderCreator
    capabilities?: ProviderMetadata['capabilities']
    validators?: ProviderMetadata['validators']
    validation?: ('health' | 'model_list')[]
    additionalHeaders?: Record<string, string>
  },
): ProviderMetadata {
  const { id, name, icon, description, nameKey, descriptionKey, category, tasks, defaultBaseUrl, creator, capabilities, validators, validation, additionalHeaders, ...rest } = options

  const finalCapabilities = capabilities || {
    listModels: async (config: Record<string, unknown>) => {
      const normalizedUrl = normalizeBaseUrl((config.baseUrl as string || ''))
      const provider = await creator(
        (config.apiKey as string || '').trim(),
        normalizedUrl,
      )

      if (!provider.model) {
        return []
      }

      return (await listModels({
        ...provider.model(),
      })).map((model: any) => {
        return {
          id: model.id,
          name: model.name || model.display_name || model.id,
          provider: id,
          description: model.description || '',
          capabilities: extractModelCapabilities(model),
          contextLength: model.context_length || 0,
          deprecated: false,
        } satisfies ModelInfo
      })
    },
  }

  const finalValidators = validators || {
    validateProviderConfig: async (config: Record<string, unknown>) => {
      if (!config.baseUrl) {
        return { errors: [], reason: '请填写服务地址（Base URL）', valid: false }
      }

      if (!isAbsoluteHttpUrl(config.baseUrl as string)) {
        return { errors: [], reason: '服务地址格式不正确，需要以 http:// 或 https:// 开头', valid: false }
      }

      const normalizedUrl = normalizeBaseUrl(config.baseUrl as string)
      const validationChecks = validation || []
      let responseModelList: Response | null = null

      if (validationChecks.includes('health')) {
        try {
          // 使用 OpenAI 兼容的只读模型列表检查服务，避免为配置校验生成计费的聊天请求。
          responseModelList = await fetch(`${normalizedUrl}models`, { headers: { Authorization: `Bearer ${config.apiKey}`, ...additionalHeaders } })
          if (!responseModelList.ok && responseModelList.status !== 404 && responseModelList.status !== 405) {
            const reason = describeHttpError(responseModelList.status, '服务检查')
            return { errors: [new Error(reason)], reason, valid: false }
          }
        }
        catch (error) {
          const detail = error instanceof Error ? `（${error.message}）` : ''
          return { errors: [], reason: `无法连接到服务器${detail}，请检查服务地址是否正确`, valid: false }
        }
      }

      const reasons: string[] = []

      if (validationChecks.includes('model_list')) {
        try {
          let response: Response | null = responseModelList
          if (!response) {
            response = await fetch(`${normalizedUrl}models`, { headers: { Authorization: `Bearer ${config.apiKey}`, ...additionalHeaders } })
          }

          if (!response.ok) {
            reasons.push(describeHttpError(response.status, '获取模型列表'))
          }
        }
        catch {
          reasons.push('获取模型列表时网络异常，请检查连接')
        }
      }

      // 去重（如两个检查都返回相同的认证错误）
      const uniqueReasons = [...new Set(reasons)]

      return {
        errors: uniqueReasons.map(r => new Error(r)),
        reason: uniqueReasons.join('\n'),
        valid: uniqueReasons.length === 0,
      }
    },
  }

  return {
    id,
    category: category || 'chat',
    tasks: tasks || ['text-generation'],
    nameKey,
    name,
    descriptionKey,
    description,
    icon,
    defaultOptions: () => ({
      baseUrl: defaultBaseUrl || '',
    }),
    createProvider: async (config) => {
      const baseUrl = config.baseUrl as string || ''
      if (!isAbsoluteHttpUrl(baseUrl)) {
        throw new Error('Base URL is not absolute. Check your input.')
      }
      return creator((config.apiKey as string || '').trim(), normalizeBaseUrl(baseUrl))
    },
    capabilities: finalCapabilities,
    validators: finalValidators,
    ...rest,
  } as ProviderMetadata
}
